import axios from "axios";
import { clearSession, getAccessToken, getRefreshToken, guardarAvisoDeLogin, renew } from "./session";

const api = axios.create({
  baseURL: process.env.REACT_APP_URL_API_NINA,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Instância separada, SEM interceptor, só para renovar.
 *
 * Usar a `api` aqui criaria recursão: o refresh tomaria 401, o interceptor tentaria renovar, e
 * assim por diante. Além disso o `/Auth/Refresh` é público — mandar um Bearer vencido nele não
 * ajuda em nada.
 */
export const bare = axios.create({ baseURL: process.env.REACT_APP_URL_API_NINA });

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error?.config;
    const status = error?.response?.status;

    // `_retried` impede laço: uma renovação por requisição. Se o token novo também for recusado
    // (conta desativada, permissão revogada), insistir seria laço infinito com o servidor.
    if (status !== 401 || !original || original._retried) return Promise.reject(error);

    // 401 `{ mustChangePassword: true, message }`: token anterior a um reset de senha. Renovar não
    // adianta (o refresh também é anterior) e acabaria em laço; encerra e leva ao login com a frase.
    if (error.response.data?.mustChangePassword === true) {
      clearSession();
      const msg = error.response.data.message;
      guardarAvisoDeLogin(
        typeof msg === "string" && msg.trim() ? msg : "Sua senha foi redefinida. Entre novamente."
      );
      window.location.assign("/auth/sign-in");
      return Promise.reject(error);
    }

    // O próprio refresh falhando não deve disparar outro refresh.
    if (typeof original.url === "string" && original.url.includes("/Auth/Refresh"))
      return Promise.reject(error);

    original._retried = true;
    const novo = await renew((body) => bare.post("/Auth/Refresh", body));

    if (!novo) {
      // Quem encerra a sessão é o `renew` (só em 400/401 do refresh). Falha transitória (429, 5xx,
      // rede) mantém a sessão: esta requisição falha normalmente e a próxima tenta renovar. Sem
      // refresh guardado não há o que tentar depois, então limpa o resto para o `PrivateRoute`
      // levar ao login.
      if (!getRefreshToken()) clearSession();
      return Promise.reject(error);
    }

    original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${novo}` };
    return api(original);
  }
);

export default api;
